import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {DoctorService} from '../../services/doctor/doctor.service';
import {iResponse} from '../../models/response/response.model';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import {DoctorSettingsModel} from '../../models/doctor-settings/doctor-settings.model';
import {MatError, MatInput, MatSuffix} from '@angular/material/input';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {NgClass} from '@angular/common';
import {CdkAccordion, CdkAccordionItem} from '@angular/cdk/accordion';
import {InfoMessageService} from '../../services/infomessage/infomessage.service';
import {
    MatDatepickerToggle,
    MatDateRangeInput,
    MatDateRangePicker,
    MatEndDate,
    MatStartDate
} from '@angular/material/datepicker';
import {
    buildDoctorSettingsForm,
    patchDoctorSettingsToForm
} from '../../../utils/doctor-settings-form/doctor-settings-form.utils';
import {convertDateToISOString, convertTimeInputToTimestamp} from '../../../utils/date-time/date-time.utils';
import {
    AngularSignaturePadModule,
    NgSignaturePadOptions,
    SignaturePadComponent
} from '@almothafar/angular-signature-pad';
import {FileUploadComponent} from '../file-upload/file-upload.component';
import {DoctorModel} from '../../models/doctor/doctor.model';
import {DeviceDetectionService} from '../../services/device-detection/device-detection.service';
import {ChangePasswordModel} from '../../models/change-password/change-password.model';
import {AuthenticationService} from '../../services/authentication/authentication.service';


export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-doctor-settings',
    imports: [
        FormsModule,
        MatInput,
        MatOption,
        MatSelect,
        ReactiveFormsModule,
        CdkAccordion,
        CdkAccordionItem,
        NgClass,
        MatDateRangeInput,
        MatStartDate,
        MatEndDate,
        MatDatepickerToggle,
        MatSuffix,
        MatDateRangePicker,
        AngularSignaturePadModule,
        FileUploadComponent,
        MatError
    ],
    templateUrl: './doctor-settings.component.html',
    styleUrl: './doctor-settings.component.sass',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoctorSettingsComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>()
    // @ts-ignore
    @ViewChild('signature') public signaturePad: SignaturePadComponent
    doctor: DoctorModel | null = null
    doctorSettings: DoctorSettingsModel = {
        consultantDurationInMinutes: 0,
        consultingHours: [],
        holidays: [],
        id: '',
        prescriptionCosts: 0,
        treatmentCosts: 0,
        customTextForEmail: '',
        shippingCostsForPrescription: 0,
        aliacuraDebtCollectionFees: 0,
        aliacuraPortalFees: 0,
        aliacuraPostage: 0,
        signature: '',
        stamp: '',
        appointmentMaxToInWeeks: 0

    }
    passwordForm: FormGroup = new FormGroup({
        newPassword: new FormControl(''),
        passwordConfirm: new FormControl(''),
    })
    doctorSettingsForm!: FormGroup;
    signaturePadOptions: NgSignaturePadOptions = {
        minWidth: 5,
        canvasWidth: 500,
        canvasHeight: 300
    }

    isMobile: boolean = false;
    timeCheckFailed: boolean = false;
    timeout: any;

    constructor(
        private fb: FormBuilder,
        private doctorService: DoctorService,
        private infoMessageService: InfoMessageService,
        private deviceDetectionService: DeviceDetectionService,
        private authenticationService: AuthenticationService
    ) {
        this.isMobile = deviceDetectionService.getIsMobile();
        this.doctorSettingsForm = buildDoctorSettingsForm(this.fb);
    }

    ngOnInit() {
        this.getMe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

    }


    get holidays(): FormArray {
        return this.doctorSettingsForm.get('holidays') as FormArray;
    }

    triggerNewHoliday(): void {
        this.holidays.push(this.fb.group({
            from: [Date, Validators.required],
            to: [Date, Validators.required]
        }));
    }


    removeHoliday(index: number): void {
        this.holidays.removeAt(index);
    }

    get consultingHours(): FormArray {
        return this.doctorSettingsForm.get('consultingHours') as FormArray;
    }

    triggerNewConsultingHour(): void {
        this.consultingHours.push(this.fb.group({
            day: ['', Validators.required],
            from: ['', Validators.required],
            to: ['', Validators.required]
        }));
    }

    removeConsultingHour(index: number): void {
        this.consultingHours.removeAt(index);
    }

    drawComplete(): void {
        const signatureBase64 = this.signaturePad.toDataURL();
        this.doctorSettings.signature = signatureBase64.replace(/^data:image\/png;base64,/, '');
        this.doctorSettingsForm.get('signature')?.patchValue(this.doctorSettings.signature);
    }

    clearStamp(): void {
        this.doctorSettingsForm.get('stamp')?.patchValue('');
    }

    clearSignature(): void {
        this.signaturePad.clear();
        this.doctorSettingsForm.get('signature')?.patchValue('');
    }

    // File-Upload
    triggerFileUpload(base64: string): void {
        this.doctorSettingsForm.get('stamp')?.patchValue(base64);
    }

    submit(): void {
        if (this.doctorSettingsForm.invalid) {
            this.doctorSettingsForm.markAllAsTouched();
            return;
        }

        if (this.timeCheckFailed) {
            this.infoMessageService.createMessage('Speichern fehlgeschlagen: Bitte prüfen Sie Ihre Öffnungszeiten', 'error')
            return;
        }

        this.doctorSettings = this.doctorSettingsForm.value;

        this.doctorSettings.consultingHours = this.consultingHours.value.map((hour: any) => ({
            day: hour.day,
            from: convertTimeInputToTimestamp(hour.from),
            to: convertTimeInputToTimestamp(hour.to)
        }));

        this.doctorSettings.consultantDurationInMinutes = this.doctorSettingsForm.get('consultantDurationInMinutes')?.value;

        this.doctorSettings.holidays = this.holidays.value.map((holiday: any) => ({
            // TODO last day 23:59:59
            from: convertDateToISOString(holiday.from),
            to: convertDateToISOString(holiday.to)
        }))

        this.saveDoctorSettings();
    }

    updatePassword(): void {
        if (this.passwordForm.invalid) {
            return;
        }
        this.passwordUpdate(this.passwordForm.get('newPassword')?.value);
    }

    checkTimeRange(consultingHour: any): void {
        this.timeCheckFailed = false;
        clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
            if (consultingHour.value.from > consultingHour.value.to) {
                this.timeCheckFailed = true;
                this.infoMessageService.createMessage('Die Startzeit ist später als die Endzeit', 'error')
                return;
            }
            if (this.diffMinutes(consultingHour.value.from, consultingHour.value.to) < +this.doctorSettingsForm.get('consultantDurationInMinutes')!.value) {
                this.timeCheckFailed = true;
                this.infoMessageService.createMessage('Die Öffnungszeiten sind kürzer als die angegebene Termindauer', 'error')
            }
        }, 500)

    }

    diffMinutes(start: string, end: string): number {
        const [h1, m1] = start.split(':').map(Number);
        const [h2, m2] = end.split(':').map(Number);

        const total1 = h1 * 60 + m1;
        const total2 = h2 * 60 + m2;

        return total2 - total1;
    }

    private passwordUpdate(password: string): void {
        const newPassword: ChangePasswordModel = {
            newPassword: password
        }
        this.authenticationService.changePassword(newPassword)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.passwordForm.reset();
                    this.infoMessageService.createMessage('Das Passwort wurde geändert', 'sucess')
                },
                error: (error) => {
                }
            })
    }

    getDoctorSettings(): void {
        this.doctorService.getDoctorSettings(this.doctor!.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<DoctorSettingsModel>) => {
                    this.doctorSettings = response.data;
                    patchDoctorSettingsToForm(this.fb, this.doctorSettingsForm, this.doctorSettings);
                    this.loadSignatureToPad();
                },
                error: (error) => {
                }
            })
    }

    loadSignatureToPad(): void {
        const signature = this.doctorSettings.signature || this.doctorSettingsForm.get('signature')?.value;
        if (signature) {
            // Wir versuchen es mehrmals, da ViewChild in einem @if erst verzögert verfügbar sein kann
            let attempts = 0;
            const tryLoad = () => {
                if (this.signaturePad) {
                    try {
                        const dataUrl = signature.startsWith('data:')
                            ? signature
                            : `data:image/png;base64,${signature}`;
                        this.signaturePad.fromDataURL(dataUrl);
                    } catch (e) {
                        console.error('Error loading signature', e);
                    }
                } else if (attempts < 10) {
                    attempts++;
                    setTimeout(tryLoad, 100);
                }
            };
            tryLoad();
        }
    }

    private saveDoctorSettings(): void {
        this.doctorService.saveDoctorSettings(this.doctor!.id, this.doctorSettings)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.infoMessageService.createMessage('Änderungen gespeichert', 'success');

                },
                error: (error) => {
                    if (error.error.error.includes('overlapping')) {
                        this.infoMessageService.createMessage('Die Öffnungszeiten überschneiden sich, bitte ändern Sie diese um das Speichern zu ermöglichen', 'error')
                    }
                    if (error.error.error.includes('invalid time span')) {
                        this.infoMessageService.createMessage('Die Öffnungszeiten stimmen nicht mit der Terminlänge überein, bitte ändern Sie diese um das Speichern zu ermöglichen', 'error')
                    }
                }
            })
    }

    private getMe(): void {
        this.doctorService.getMyProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<DoctorModel>) => {
                    this.doctor = response.data;
                    this.getDoctorSettings();
                },
                error: (error) => {
                    console.error(error)
                }
            })
    }

}
